import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, fetchAllPages } from "../api/client";
import {
  createProject,
  getProject,
  getProjectDashboard,
  listMilestones,
  listProjects,
  listWorkItems,
  updateProject,
  type PmProject,
  type PmProjectFields,
  type PmProjectInput,
} from "../api/pm";

const PROJECTS_KEY = ["pm", "projects"];

export function useProjects() {
  return useQuery({
    queryKey: PROJECTS_KEY,
    queryFn: () => fetchAllPages((cursor) => listProjects(cursor)),
    staleTime: 60_000,
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ["pm", "project", id],
    queryFn: () => getProject(id as string),
    enabled: Boolean(id),
    staleTime: 60_000,
    // Un refus ou une absence ne changent pas au second essai.
    retry: (count, e) =>
      !(e instanceof ApiError && (e.status === 403 || e.status === 404)) &&
      count < 1,
  });
}

export function useProjectWorkItems(id: string | undefined) {
  return useQuery({
    queryKey: ["pm", "work-items", id],
    queryFn: () =>
      fetchAllPages((cursor) => listWorkItems(id as string, cursor)),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useProjectMilestones(id: string | undefined) {
  return useQuery({
    queryKey: ["pm", "milestones", id],
    queryFn: () =>
      fetchAllPages((cursor) => listMilestones(id as string, cursor)),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useProjectDashboard(id: string | undefined) {
  return useQuery({
    queryKey: ["pm", "dashboard", id],
    queryFn: () => getProjectDashboard(id as string),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

function useProjectMutation<A>(mutationFn: (a: A) => Promise<PmProject>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (p) => {
      qc.setQueryData(["pm", "project", p.id], p);
      void qc.invalidateQueries({ queryKey: PROJECTS_KEY });
      void qc.invalidateQueries({ queryKey: ["pm", "dashboard", p.id] });
    },
  });
}

export function useCreateProject() {
  return useProjectMutation((input: PmProjectInput) => createProject(input));
}

export function useUpdateProject() {
  return useProjectMutation(
    ({ id, patch }: { id: string; patch: Partial<PmProjectFields> }) =>
      updateProject(id, patch),
  );
}
